import DateFnsUtils from '@date-io/date-fns';
import { useElementSize } from '@mantine/hooks';
import { makeStyles, Theme } from '@material-ui/core';
import {
  KeyboardDatePicker,
  KeyboardDatePickerProps,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import COLORS_FLAT from '@styles/colors/themes/beam/beam-flat';
import TYPOGRAPHY_FLAT from '@styles/fonts/fontsizes/typography-flat';

import { formatDate } from '@utils';
import { CSSProperties } from 'styled-components';
import { DateDiv } from './DateInput.styles';

export const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';

interface OwnProps
  extends Partial<Exclude<KeyboardDatePickerProps, 'className'>> {
  value: Date | null;
  handleDateChange: (date: string | null) => void;
  width?: CSSProperties['width'];
  height?: number;
  borderColor?: string;
  divProps?: {
    'data-active'?: boolean;
    'data-error'?: boolean;
    onClick?: React.MouseEventHandler;
  };
  pinnedData?: boolean;
  direction?: string;
}

const usePopoverStyles = makeStyles<
  Theme,
  { width: string | undefined },
  'paper'
>({
  paper: {
    '& .MuiPickersToolbar-toolbar': {
      backgroundColor: COLORS_FLAT.n200,
      '& .MuiPickersToolbarText-toolbarTxt': {
        ...TYPOGRAPHY_FLAT.h800,
      },
      '& .MuiTypography-subtitle1': {
        ...TYPOGRAPHY_FLAT.p300,
        color: COLORS_FLAT.n700,
      },
    },
    '& .MuiPickersMonthSelection-container': {
      alignSelf: 'center',
    },
    '& .MuiPickersBasePicker-pickerView': {
      minWidth: '286px',
      // min padding that the date icons fit with
      padding: 4,
      maxWidth: 'unset',
      width: props => props.width,
    },
    '& .MuiToolbar-gutters': {
      rowGap: 8,
    },
  },
});
/**
 * FIXME: minDate and maxDate will behave weirdly with null. Fixed in: https://github.com/mui/mui-x/releases/tag/v5.16.0 , update mui or migrate away.
 */
export const DateInput = (props: OwnProps) => {
  const {
    value,
    handleDateChange,
    borderColor,
    width,
    height = 42,
    className,
    divProps = {},
    pinnedData,
    direction,
    ...rest
  } = props;

  const { ref: divRef, width: divWidth } = useElementSize();
  const widthTrf = typeof width === 'number' ? `${width}px` : width;
  const propVal = `${divWidth}px`;
  const classes = usePopoverStyles({ width: propVal });

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      {/* Todo: See if DateDIv can be removed and we just use ClassName prop. */}
      <DateDiv
        ref={divRef}
        $width={widthTrf}
        height={height}
        borderColor={borderColor}
        className={className}
        pinnedData={pinnedData}
        dir={direction}
        {...divProps}
      >
        <KeyboardDatePicker
          InputProps={{
            disableUnderline: true,
          }}
          variant="inline"
          autoOk
          openTo="date"
          fullWidth
          format={DEFAULT_DATE_FORMAT}
          id="date-picker-inline"
          value={value}
          onChange={(e: MaterialUiPickersDate) => {
            if (e === null || e.getTime()) {
              // Only change date if a valid date is given.
              // todo: pass date directly if all users expect Date
              handleDateChange(formatDate(e));
            }
          }}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
          PopoverProps={{
            classes,
            // Anchor is Input itself.
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            ...rest.PopoverProps,
          }}
          views={['year', 'month', 'date']}
          maxDateMessage=""
          minDateMessage=""
          InputAdornmentProps={pinnedData ? { position: 'start' } : undefined}
          {...rest}
        />
      </DateDiv>
    </MuiPickersUtilsProvider>
  );
};
export type { OwnProps as DateInputProps };
